import React, { useState, useEffect } from "react";
import {
  Monitor,
  RefreshCw,
  ShieldAlert,
  Clock,
  Laptop,
} from "lucide-react";
import { useMasterAdmin } from "../../context/MasterAdminAuthContext";
import { toast } from "sonner";

export default function MasterAdminDevices() {
  const { masterApi } = useMasterAdmin();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDevices = async () => {
    try {
      setLoading(true);
      const res = await masterApi.get("/master-admin/devices");
      setDevices(res.data.devices || []);
    } catch (err) {
      toast.error("Failed to load devices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices();
  }, []);

  const handleUnbind = async (device) => {
    if (!window.confirm(`Unbind machine ${device.machine_name}? The user will be able to bind a new machine.`)) {
      return;
    }
    try {
      await masterApi.post(`/master-admin/devices/${device.device_id}/unbind`);
      toast.success(`Machine ${device.machine_name} unbound successfully`);
      fetchDevices();
    } catch (err) {
      toast.error("Failed to unbind device");
    }
  };

  return (
    <div className="flex flex-col gap-6 max-w-7xl mx-auto pb-10">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-black text-white tracking-tight flex items-center gap-2">
            <span>Hardware Devices</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 font-bold">
              {devices.length} Registered Machines
            </span>
          </h1>
          <p className="text-xs text-zinc-400 mt-0.5">
            Registered PC hardware fingerprints, operating systems, and network IP addresses.
          </p>
        </div>

        <button
          onClick={fetchDevices}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-xs font-semibold text-zinc-300 hover:text-white transition-all"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Devices Table */}
      <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl overflow-hidden shadow-xl">
        {loading ? (
          <div className="flex items-center justify-center h-48 text-zinc-400">
            <div className="w-6 h-6 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : devices.length === 0 ? (
          <div className="p-10 text-center text-xs text-zinc-500">
            No registered devices found. Devices are bound automatically when users log in on a Windows PC.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="bg-zinc-950/60 text-zinc-400 border-b border-zinc-800 font-medium">
                  <th className="py-3 px-4">Machine Name</th>
                  <th className="py-3 px-4">User</th>
                  <th className="py-3 px-4">OS & App Version</th>
                  <th className="py-3 px-4">Fingerprint Hash</th>
                  <th className="py-3 px-4">Last Seen</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/60">
                {devices.map((dev) => {
                  const isBound = dev.status === "bound";
                  return (
                    <tr key={dev.device_id} className="hover:bg-zinc-800/30 transition-colors">
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 shrink-0">
                            <Laptop className="w-4 h-4" />
                          </div>
                          <div>
                            <div className="font-bold text-white">{dev.machine_name || "Windows PC"}</div>
                            <div className="text-[10px] text-zinc-500 font-mono">IP: {dev.last_ip || "127.0.0.1"}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3.5 px-4 font-mono font-bold text-zinc-300">@{dev.username}</td>
                      <td className="py-3.5 px-4">
                        <div className="text-zinc-200 font-medium">{dev.os_name || "Windows"}</div>
                        <div className="text-[10px] text-zinc-500">v{dev.app_version || "1.0.0"}</div>
                      </td>
                      <td className="py-3.5 px-4 font-mono text-zinc-400 text-[11px]">
                        {dev.fingerprint_hash ? `${dev.fingerprint_hash.slice(0, 16)}...` : "N/A"}
                      </td>
                      <td className="py-3.5 px-4 text-zinc-400 font-mono">
                        {dev.last_seen_at ? new Date(dev.last_seen_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "N/A"}
                      </td>
                      <td className="py-3.5 px-4">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                            isBound
                              ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                              : "bg-zinc-800 text-zinc-400 border border-zinc-700"
                          }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${isBound ? "bg-emerald-400" : "bg-zinc-500"}`} />
                          <span className="capitalize">{dev.status}</span>
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-right">
                        {isBound && (
                          <button
                            onClick={() => handleUnbind(dev)}
                            className="px-2.5 py-1 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-[11px] font-medium transition-colors"
                          >
                            Unbind
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
